/**
 * Capture a poster frame from a local video File for thumbnail preview/upload.
 */
export function captureVideoThumbnail(file: File, seekSeconds = 1): Promise<Blob> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.src = url;

  return captureFrameFromVideoElement(video, seekSeconds, () => {
    URL.revokeObjectURL(url);
    video.removeAttribute('src');
    video.load();
  });
}

/** Capture a poster frame from a remote http(s) video URL (best-effort; may fail on CORS). */
export function captureVideoThumbnailFromUrl(videoUrl: string, seekSeconds = 1): Promise<Blob> {
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = 'anonymous';
  video.src = videoUrl;

  return captureFrameFromVideoElement(video, seekSeconds, () => {
    video.removeAttribute('src');
    video.load();
  });
}

function captureFrameFromVideoElement(
  video: HTMLVideoElement,
  seekSeconds: number,
  cleanup: () => void,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    video.addEventListener('error', () => {
      cleanup();
      reject(new Error('Failed to read video for thumbnail.'));
    });

    video.addEventListener('loadeddata', () => {
      const targetTime = Math.min(seekSeconds, Math.max(0, (video.duration || seekSeconds) - 0.1));
      const seekHandler = () => {
        video.removeEventListener('seeked', seekHandler);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas unavailable');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              cleanup();
              if (!blob) {
                reject(new Error('Failed to generate thumbnail.'));
                return;
              }
              resolve(blob);
            },
            'image/jpeg',
            0.85,
          );
        } catch (err) {
          cleanup();
          reject(err instanceof Error ? err : new Error('Failed to generate thumbnail.'));
        }
      };

      if (Number.isFinite(targetTime) && targetTime > 0) {
        video.addEventListener('seeked', seekHandler);
        video.currentTime = targetTime;
      } else {
        seekHandler();
      }
    });
  });
}
